import React, { useEffect, useMemo, useRef, useState } from "react"
import { Container, Card, CardBody, Modal, Label, Input } from "reactstrap"
import { useTable } from "react-table"
import NewPagination from "components/Common/Newpagination"
import Select from "react-select"
import Swal from "sweetalert2"
import axios from "axios"
import configure from "configure"
import Breadcrumbs from "components/Common/Breadcrumb"
import Loader from "pages/Separate/Loader"
import secureLocalStorage from "react-secure-storage"
import { useNavigate } from "react-router-dom"
import { fetchNodeData, FetchNodePostData, FetchNodePutData } from "utils/common-fetchData"

const PHONE_REGEX = /^\d{10}$/

const EMPTY_FORM = {
  id: null,
  name: "",
  email: "",
  phone: "",
  password: "",
  tier: "external",
  exams: [],
  sops: [],
}

const TraineeManagement = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [trainees, setTrainees] = useState([])
  const [total, setTotal] = useState(0)
  const [counts, setCounts] = useState({ all: 0, external: 0, internal: 0 })
  const [examOptions, setExamOptions] = useState([])
  const [sopOptions, setSopOptions] = useState([])
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [tierFilter, setTierFilter] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const [modal, setModal] = useState(false)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const [convertModal, setConvertModal] = useState(false)
  const [selectedTrainee, setSelectedTrainee] = useState(null)
  const [convertForm, setConvertForm] = useState({ gender: "", phone_number: "", department: "", designation: "", roleid: "" })
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [convertOptionsLoading, setConvertOptionsLoading] = useState(false)

  useEffect(() => {
    document.title = "Trainee Management | Zithas Technologies"
    fetchExamOptions()
    fetchSopOptions()
  }, [])

  // Debounce search so we don't hit the server on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350)
    return () => clearTimeout(t)
  }, [search])

  // Any filter change should snap back to page 1
  // Combined reset+fetch effect: a separate "reset page" + "fetch" effect let a
  // filter change fire a fetch with the stale pre-reset page number, and if that
  // stale request resolved after the correct one, it silently overwrote the
  // correctly-filtered results (looked like "search not working").
  const prevFiltersRef = useRef({ debouncedSearch, tierFilter, pageSize })
  useEffect(() => {
    const prev = prevFiltersRef.current
    const filtersChanged =
      prev.debouncedSearch !== debouncedSearch ||
      prev.tierFilter !== tierFilter ||
      prev.pageSize !== pageSize
    prevFiltersRef.current = { debouncedSearch, tierFilter, pageSize }

    if (filtersChanged && page !== 1) {
      setPage(1)
      return
    }
    fetchTrainees()
  }, [page, pageSize, tierFilter, debouncedSearch])

  const fetchTrainees = async () => {
    const token = secureLocalStorage.getItem("auth_token")
    const userId = localStorage.getItem("auth_id")
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    if (debouncedSearch) params.set("search", debouncedSearch)
    if (tierFilter) params.set("tier", tierFilter)
    const res = await fetchNodeData(setLoading, `trainee/getAll/${userId}?${params.toString()}`, token)
    if (res?.success) {
      setTrainees(res.data || [])
      setTotal(res.total || 0)
      setCounts(res.counts || { all: 0, external: 0, internal: 0 })
    }
  }

  const fetchExamOptions = async () => {
    const token = secureLocalStorage.getItem("auth_token")
    const userId = localStorage.getItem("auth_id")
    const res = await fetchNodeData(null, `preassesment/test/get/${userId}`, token)
    if (res?.success) {
      setExamOptions((res.data || []).map(t => ({
        value: t.id,
        label: `${t.test_name} (${Number(t.test_type) === 1 ? "Mock" : "Actual"})`,
      })))
    }
  }

  const fetchSopOptions = async () => {
    const token = secureLocalStorage.getItem("auth_token")
    const res = await fetchNodeData(null, "sop/folderAccess", token)
    if (res?.success) setSopOptions((res.data || []).map(s => ({ value: s.id, label: s.folder_label })))
  }

  const countCsv = csv => (csv ? String(csv).split(",").filter(Boolean).length : 0)

  const openAddModal = () => {
    setFormData(EMPTY_FORM)
    setModal(true)
  }

  const openEditModal = t => {
    setFormData({
      id: t.id,
      name: t.name || "",
      email: t.email || "",
      phone: t.phone || "",
      password: "",
      tier: t.tier || "external",
      exams: examOptions.filter(o => (t.assigned_exam_ids || "").split(",").map(s => s.trim()).includes(String(o.value))),
      sops: sopOptions.filter(o => (t.assigned_sop_ids || "").split(",").map(s => s.trim()).includes(String(o.value))),
    })
    setModal(true)
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!formData.name.trim() || !formData.email.trim() || (!formData.id && !formData.password)) {
      Swal.fire({ icon: "warning", title: "Name, email and password are required", confirmButtonColor: "#752a97" })
      return
    }
    if (formData.phone && !PHONE_REGEX.test(formData.phone)) {
      Swal.fire({ icon: "warning", title: "Phone number must be exactly 10 digits", confirmButtonColor: "#752a97" })
      return
    }

    setSaving(true)
    const token = secureLocalStorage.getItem("auth_token")
    const userId = localStorage.getItem("auth_id")
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone || "",
      tier: formData.tier,
      exam_ids: formData.exams.map(o => o.value).join(","),
      sop_ids: formData.sops.map(o => o.value).join(","),
      user_id: userId,
    }
    if (formData.password) payload.password = formData.password

    const res = formData.id
      ? await FetchNodePutData(setSaving, "trainee/edit", formData.id, payload)
      : await FetchNodePostData(setSaving, "trainee/create", token, payload)

    if (res?.data?.success) {
      Swal.fire({
        icon: "success",
        title: formData.id ? "Trainee updated successfully" : "Trainee created successfully",
        timer: 1500,
        showConfirmButton: false,
      })
      setModal(false)
      fetchTrainees()
    } else {
      Swal.fire({ icon: "error", title: "Save failed", text: res?.data?.message, confirmButtonColor: "#752a97" })
    }
  }

  const handlePromote = async t => {
    const confirm = await Swal.fire({
      icon: "question",
      title: "Promote to Internal Trainee?",
      text: `${t.name} will move from External to Internal Trainee.`,
      showCancelButton: true,
      confirmButtonText: "Yes, promote",
      confirmButtonColor: "#752a97",
    })
    if (!confirm.isConfirmed) return

    setSaving(true)
    const userId = localStorage.getItem("auth_id")
    const res = await FetchNodePutData(setSaving, "trainee/promote", t.id, { user_id: userId })
    if (res?.data?.success) {
      Swal.fire({ icon: "success", title: "Promoted to Internal Trainee", timer: 1300, showConfirmButton: false })
      fetchTrainees()
    } else {
      Swal.fire({ icon: "error", title: "Promotion failed", text: res?.data?.message, confirmButtonColor: "#752a97" })
    }
  }

  const loadConvertOptions = async () => {
    setConvertOptionsLoading(true)
    try {
      const [deptRes, roleRes] = await Promise.all([
        axios.get(`${configure.nodeApi}/departments`, { headers: { Accept: "application/json" } }),
        axios.get(`${configure.nodeApi}/staffData`, { headers: { Accept: "application/json" } }),
      ])
      setDepartmentOptions(deptRes.data?.data?.department || [])
      setRoleOptions(roleRes.data?.data?.roles || [])
    } catch (e) {
      console.warn("convert options fetch error", e)
    }
    setConvertOptionsLoading(false)
  }

  const openConvertModal = t => {
    setSelectedTrainee(t)
    setConvertForm({ gender: "", phone_number: t.phone || "", department: "", designation: "", roleid: "" })
    setConvertModal(true)
    loadConvertOptions()
  }

  const handleConvertSubmit = async e => {
    e.preventDefault()
    if (!convertForm.gender.trim() || !convertForm.phone_number.trim()) {
      Swal.fire({ icon: "warning", title: "Gender and phone number are required", confirmButtonColor: "#752a97" })
      return
    }
    if (!PHONE_REGEX.test(convertForm.phone_number.trim())) {
      Swal.fire({ icon: "warning", title: "Phone number must be exactly 10 digits", confirmButtonColor: "#752a97" })
      return
    }
    setSaving(true)
    const token = secureLocalStorage.getItem("auth_token")
    const userId = localStorage.getItem("auth_id")
    const res = await FetchNodePostData(setSaving, `trainee/convertToStaff/${selectedTrainee.id}`, token, {
      user_id: userId,
      gender: convertForm.gender.trim(),
      phone_number: convertForm.phone_number.trim(),
      department: convertForm.department || "",
      designation: convertForm.designation || "",
      roleid: convertForm.roleid || "",
    })
    if (res?.data?.success) {
      const newStaffId = res.data.data?.user_id
      setConvertModal(false)
      await Swal.fire({
        icon: "success",
        title: "Converted to Staff successfully",
        text: newStaffId ? "Redirecting to the staff record to finish the remaining details…" : undefined,
        timer: 1500,
        showConfirmButton: false,
      })
      if (newStaffId) {
        navigate(`${configure.appUrl}edit-staff/${newStaffId}`)
      } else {
        fetchTrainees()
      }
    } else {
      Swal.fire({ icon: "error", title: "Conversion failed", text: res?.data?.message, confirmButtonColor: "#752a97" })
    }
  }

  const handleGenerateDoc = async t => {
    const confirm = await Swal.fire({
      icon: "question",
      title: "Generate Training Certificate?",
      text: `This will record today as the certificate generation date for ${t.name}.`,
      showCancelButton: true,
      confirmButtonText: "Yes, generate",
      confirmButtonColor: "#752a97",
    })
    if (!confirm.isConfirmed) return

    setSaving(true)
    const userId = localStorage.getItem("auth_id")
    const res = await FetchNodePutData(setSaving, "trainee/generate-doc", t.id, { user_id: userId })
    if (res?.data?.success) {
      Swal.fire({ icon: "success", title: "Certificate date recorded", timer: 1300, showConfirmButton: false })
      fetchTrainees()
    } else {
      Swal.fire({ icon: "error", title: "Failed", text: res?.data?.message, confirmButtonColor: "#752a97" })
    }
  }

  const hasFilters = search || tierFilter

  const columns = useMemo(() => [
    {
      Header: "Name / Email",
      accessor: "name",
      Cell: ({ row }) => (
        <>
          <div style={{ fontWeight: 600, color: "#1f2937" }}>{row.original.name}</div>
          <div style={{ fontSize: 11, color: "#6b7280" }}>{row.original.email}</div>
        </>
      ),
    },
    {
      Header: "Phone",
      accessor: "phone",
      Cell: ({ value }) => value || "—",
    },
    {
      Header: "Tier",
      accessor: "tier",
      className: "text-center",
      Cell: ({ value, row }) => (
        <>
          <span style={{
            display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
            background: value === "internal" ? "#f3e8ff" : "#f3f4f6",
            color: value === "internal" ? "#752a97" : "#6b7280",
            border: `1px solid ${value === "internal" ? "#d8b4fe" : "#d1d5db"}`,
          }}>
            {value === "internal" ? "Internal Trainee" : "External Trainee"}
          </span>
          {value === "internal" && row.original.promoted_from_external ? (
            <div style={{ fontSize: 10, color: "#9333ea", marginTop: 3 }}>
              <i className="fas fa-arrow-up me-1"></i>Converted from External
            </div>
          ) : null}
        </>
      ),
    },
    {
      Header: "Exams",
      accessor: "assigned_exam_ids",
      className: "text-center",
      Cell: ({ value }) => countCsv(value),
    },
    {
      Header: "Documents",
      accessor: "assigned_sop_ids",
      className: "text-center",
      Cell: ({ value }) => countCsv(value),
    },
    {
      Header: "Certificate",
      accessor: "doc_generated",
      className: "text-center",
      Cell: ({ value, row }) => value
        ? (
          <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 600 }}>
            <i className="fas fa-check-circle me-1"></i>
            {new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
          </span>
        ) : (
          <button
            className="btn btn-sm"
            style={{ border: "1px solid #c9a227", color: "#a07800", background: "transparent", fontSize: 11 }}
            onClick={() => handleGenerateDoc(row.original)}
          >
            <i className="fas fa-certificate me-1"></i>Generate
          </button>
        ),
    },
    {
      Header: "Action",
      id: "action",
      className: "text-center",
      Cell: ({ row }) => {
        const t = row.original
        return (
          <div className="d-flex gap-1 justify-content-center">
            <button className="btn btn-sm btn-outline-secondary" title="Edit" onClick={() => openEditModal(t)}>
              <i className="fas fa-pencil-alt"></i>
            </button>
            {t.tier === "external" ? (
              <button className="btn btn-sm" style={{ border: "1px solid #752a97", color: "#752a97", background: "transparent" }}
                onClick={() => handlePromote(t)}>
                <i className="fas fa-arrow-up me-1"></i>Promote
              </button>
            ) : (
              <button className="btn btn-sm" style={{ border: "1px solid #16a34a", color: "#16a34a", background: "transparent" }}
                onClick={() => openConvertModal(t)}>
                <i className="fas fa-user-check me-1"></i>Convert to Staff
              </button>
            )}
          </div>
        )
      },
    },
  ], [examOptions, sopOptions])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data: trainees })

  // Pagination is server-driven — adapt our page/pageSize/total state to NewPagination's expected shape
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const pageIndex = page - 1

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div className="page-content mb-4">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Trainee Management" />
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                  <div style={{
                    width: 34, height: 34, borderRadius: 8,
                    background: "linear-gradient(135deg, #752a97, #a855c9)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <i className="fas fa-user-graduate text-white" style={{ fontSize: 15 }}></i>
                  </div>
                  <span className="fw-bold text-purple" style={{ fontSize: 15 }}>Trainee Management</span>
                </div>
                <button className="border-radius btn btn-purple btn-sm" onClick={openAddModal}>
                  <i className="fas fa-plus me-2"></i>Add Trainee
                </button>
              </div>

              <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
                <div className="d-flex gap-2">
                  {[
                    { key: "", label: "All", count: counts.all },
                    { key: "external", label: "External Trainee", count: counts.external },
                    { key: "internal", label: "Internal Trainee", count: counts.internal },
                  ].map(tab => (
                    <button
                      key={tab.key || "all"}
                      type="button"
                      onClick={() => setTierFilter(tab.key)}
                      style={{
                        padding: "5px 16px",
                        borderRadius: 20,
                        border: tierFilter === tab.key ? "none" : "1px solid #d8b4fe",
                        background: tierFilter === tab.key ? "linear-gradient(135deg, #752a97, #a855c9)" : "transparent",
                        color: tierFilter === tab.key ? "#fff" : "#752a97",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {tab.label}
                      <span style={{
                        background: tierFilter === tab.key ? "rgba(255,255,255,0.3)" : "#f3e8ff",
                        color: tierFilter === tab.key ? "#fff" : "#752a97",
                        borderRadius: 10,
                        padding: "1px 7px",
                        fontSize: 11,
                        fontWeight: 700,
                      }}>
                        {tab.count}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="position-relative" style={{ flex: "1 1 220px", maxWidth: 260 }}>
                    <i className="fas fa-search position-absolute" style={{ left: 10, top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: 12 }}></i>
                    <input
                      className="form-control"
                      style={{ paddingLeft: 30, fontSize: 13, borderRadius: 8, height: 36 }}
                      placeholder="Search name or email..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  <select className="form-select" style={{ width: 100, fontSize: 13, height: 36 }}
                    value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                    {[10, 20, 30, 50].map(size => (
                      <option key={size} value={size}>Show {size}</option>
                    ))}
                  </select>
                  {hasFilters && (
                    <button className="btn btn-sm rounded-4 btn-red" onClick={() => { setSearch(""); setTierFilter("") }}>
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>

              {trainees.length === 0 && !loading ? (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-user-graduate fa-3x mb-3 d-block"></i>
                  <p>{hasFilters ? "No trainees match the current filters." : "No trainees yet. Click Add Trainee to get started."}</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0" style={{ fontSize: 13 }} {...getTableProps()}>
                      <thead className="table-light">
                        {headerGroups.map((headerGroup, i) => (
                          <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column, index) => (
                              <th key={index} className={column.className} {...column.getHeaderProps()}>
                                {column.render("Header")}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {rows.map((row, i) => {
                          prepareRow(row)
                          return (
                            <tr key={i} className="row-hover" {...row.getRowProps()}>
                              {row.cells.map((cell, index) => (
                                <td key={index} className={cell.column.className} style={{ verticalAlign: "middle" }} {...cell.getCellProps()}>
                                  {cell.render("Cell")}
                                </td>
                              ))}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  <NewPagination
                    gotoPage={p => setPage(p + 1)}
                    canPreviousPage={page > 1}
                    previousPage={() => setPage(p => Math.max(1, p - 1))}
                    canNextPage={page < totalPages}
                    pageIndex={pageIndex}
                    pageOptions={Array.from({ length: totalPages })}
                    pageSize={pageSize}
                    page={rows}
                    data={{ length: total }}
                    nextPage={() => setPage(p => Math.min(totalPages, p + 1))}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Add / Edit Trainee Modal */}
      <Modal isOpen={modal} size="xl" centered toggle={() => setModal(false)}>
        <div className="modal-header px-4 py-3">
          <h5 className="modal-title mt-0" style={{ fontSize: 15 }}>
            <i className={`fas ${formData.id ? "fa-pencil-alt" : "fa-user-plus"} me-2 text-purple`}></i>
            {formData.id ? "Edit Trainee" : "Add Trainee"}
          </h5>
          <button type="button" onClick={() => setModal(false)} className="btn-modal-close">
            <span aria-hidden="true" className="btn-modal-span">&times;</span>
          </button>
        </div>
        <div className="modal-body px-4 py-3">
          <form onSubmit={handleSubmit}>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Name <span className="text-danger">*</span></Label>
                <Input type="text" value={formData.name} onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))} required />
              </div>
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Email <span className="text-danger">*</span></Label>
                <Input type="email" value={formData.email} onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))} required />
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Phone</Label>
                <Input
                  type="text"
                  maxLength={10}
                  value={formData.phone}
                  onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                />
              </div>
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Password {!formData.id && <span className="text-danger">*</span>}
                  {formData.id && <span className="text-muted ms-1" style={{ fontWeight: 400, fontSize: 11 }}>(leave blank to keep existing)</span>}
                </Label>
                <Input type="password" value={formData.password} onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))} required={!formData.id} />
              </div>
            </div>

            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>Tier</Label>
              <div className="d-flex gap-2">
                {[{ key: "external", label: "External Trainee" }, { key: "internal", label: "Internal Trainee" }].map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, tier: t.key }))}
                    style={{
                      padding: "5px 16px",
                      borderRadius: 20,
                      border: formData.tier === t.key ? "none" : "1px solid #d8b4fe",
                      background: formData.tier === t.key ? "linear-gradient(135deg, #752a97, #a855c9)" : "transparent",
                      color: formData.tier === t.key ? "#fff" : "#752a97",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>Assign Exams</Label>
              <Select
                placeholder="Select exams…"
                className="basic-multi-select"
                classNamePrefix="select"
                options={examOptions}
                value={formData.exams}
                onChange={val => setFormData(prev => ({ ...prev, exams: val || [] }))}
                isMulti
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </div>

            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>Assign Training Docs</Label>
              <Select
                placeholder="Select training docs…"
                className="basic-multi-select"
                classNamePrefix="select"
                options={sopOptions}
                value={formData.sops}
                onChange={val => setFormData(prev => ({ ...prev, sops: val || [] }))}
                isMulti
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </div>

            <div className="mt-4 pt-3" style={{ borderTop: "1px solid #e9ecef" }}>
              <button type="submit" className="border-radius btn btn-purple btn-sm px-4" disabled={saving}>
                <i className={`fas ${formData.id ? "fa-save" : "fa-user-plus"} me-2`}></i>
                {saving ? "Saving…" : formData.id ? "Update Trainee" : "Create Trainee"}
              </button>
              <button type="button" className="border-radius btn btn-light btn-sm px-4 ms-2" onClick={() => setModal(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Convert to Staff Modal */}
      <Modal isOpen={convertModal} size="lg" centered toggle={() => setConvertModal(false)}>
        <div className="modal-header px-4 py-3">
          <h5 className="modal-title mt-0" style={{ fontSize: 15 }}>
            <i className="fas fa-user-check me-2 text-purple"></i>
            Convert to Staff
          </h5>
          <button type="button" onClick={() => setConvertModal(false)} className="btn-modal-close">
            <span aria-hidden="true" className="btn-modal-span">&times;</span>
          </button>
        </div>
        <div className="modal-body px-4 py-3">
          {selectedTrainee && (
            <div className="mb-3" style={{ fontSize: 12, color: "#6b7280" }}>
              Converting <strong>{selectedTrainee.name}</strong> ({selectedTrainee.email}) into a real Staff record.
            </div>
          )}
          <form onSubmit={handleConvertSubmit}>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Gender <span className="text-danger">*</span></Label>
                <Input
                  type="select"
                  value={convertForm.gender}
                  onChange={e => setConvertForm(prev => ({ ...prev, gender: e.target.value }))}
                  required
                >
                  <option value="">Select…</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </Input>
              </div>
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Phone Number <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  maxLength={10}
                  value={convertForm.phone_number}
                  onChange={e => setConvertForm(prev => ({ ...prev, phone_number: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                  required
                />
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Department</Label>
                <Input
                  type="select"
                  value={convertForm.department}
                  onChange={e => setConvertForm(prev => ({ ...prev, department: e.target.value }))}
                  disabled={convertOptionsLoading}
                >
                  <option value="">{convertOptionsLoading ? "Loading…" : "Select…"}</option>
                  {departmentOptions.map(d => (
                    <option key={d.id} value={d.id}>{d.department_name}</option>
                  ))}
                </Input>
              </div>
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Role</Label>
                <Input
                  type="select"
                  value={convertForm.roleid}
                  onChange={e => setConvertForm(prev => ({ ...prev, roleid: e.target.value }))}
                  disabled={convertOptionsLoading}
                >
                  <option value="">{convertOptionsLoading ? "Loading…" : "Select…"}</option>
                  {roleOptions.map(r => (
                    <option key={r.id} value={r.id}>{r.role_name}</option>
                  ))}
                </Input>
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Designation</Label>
                <Input type="text" value={convertForm.designation} onChange={e => setConvertForm(prev => ({ ...prev, designation: e.target.value }))} />
              </div>
            </div>
            <div className="mt-4 pt-3" style={{ borderTop: "1px solid #e9ecef" }}>
              <button type="submit" className="border-radius btn btn-purple btn-sm px-4" disabled={saving}>
                <i className="fas fa-user-check me-2"></i>
                {saving ? "Converting…" : "Convert to Staff"}
              </button>
              <button type="button" className="border-radius btn btn-light btn-sm px-4 ms-2" onClick={() => setConvertModal(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </React.Fragment>
  )
}

export default TraineeManagement
